import { Navigate } from 'react-router';
import { useAuthStore } from '@/stores/auth-store';
import { useAppVariant } from '@/lib/app-variant';
import { useMdmKillSwitch } from '@/hooks/useMdmKillSwitch';
import { usePermissions } from '@/hooks/usePermissions';
import MdmAccessDeniedPage from '@/features/mdm-access-denied/MdmAccessDeniedPage';

/**
 * Route guard for OptiEdge MDM pages (`/app/mdm/**`).
 *
 * Two checks combined (spec 19 admin-only model — Phase A):
 *   1. **Plan gate** — `company.feature.mdm` must be true (Pro Plus+ MDM SKU).
 *      Skipped on the optiedge variant (the variant itself is the entitlement).
 *   2. **Role gate** — the user must be Owner or Super Admin. Devices land in
 *      the Default team; the Owner re-distributes via Move-to-Team and
 *      Share-Folder afterwards. Phase B will introduce a custom MDM Admin role.
 *
 * Failure paths differ per variant:
 *   - **optisigns**: redirect to `/app/screenManagement` (signage app still
 *     accessible without MDM).
 *   - **optiedge**: render `MdmAccessDeniedPage` (no signage app to fall
 *     back to; show a helpful "contact your administrator" message).
 *
 * Waits for auth/company to load to avoid flashing the redirect during boot.
 */
export function MdmFeatureRoute({ children }: { children: React.ReactNode }) {
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const variant = useAppVariant();
  const mdmKillSwitchOn = useMdmKillSwitch();
  const { isRootSuperAdmin } = usePermissions();

  // While user data is loading, don't flash redirect.
  if (!me) return null;

  // Plan gate (optisigns only — optiedge variant is itself the entitlement).
  // Two ways in: per-account `company.feature.mdm` (Sales-set whitelist) OR
  // the global kill-switch `appCfgs.mdmEnabled` (mass-rollout switch). Mirrors
  // the OR check in TopNavigation per Henry's commit 6aad35445.
  // feature.mdm is a new OptiEdge-Phase-1 company flag — not yet in the
  // CompanyFeatures TypeScript type. Read it via a narrow structural check.
  if (variant === 'optisigns') {
    const hasMdmFeature = (company?.feature as Record<string, unknown> | undefined)?.mdm === true;
    if (!hasMdmFeature && !mdmKillSwitchOn) {
      return <Navigate to="/app/screenManagement" replace />;
    }
  }

  // Role gate (both variants). Owner / Super Admin only — Phase B will add
  // custom MDM Admin role via team.permissions.mdmCanManage.
  if (!isRootSuperAdmin) {
    if (variant === 'optiedge') {
      return <MdmAccessDeniedPage />;
    }
    return <Navigate to="/app/screenManagement" replace />;
  }

  return <>{children}</>;
}
